import React from "react";
import { Button, Checkbox, Spinner, Switch, Text, Tooltip } from "@fluentui/react-components";
import { PlugDisconnected16Regular, Link16Regular, PersonAdd16Regular, Person16Regular, Info16Regular } from "@fluentui/react-icons";
import { createColumnHelper } from "@tanstack/react-table";

const columnHelper = createColumnHelper();

/**
 * Build memoization-safe column definitions for the Queue table.
 *
 * @param {object}   opts
 * @param {function}  opts.onUnlink                - (row: object) => void
 * @param {function}  opts.onLink                  - (row: object) => void
 * @param {function}  opts.onAssignMissedCalls     - (rows: object[]) => void
 * @param {object}    opts.missedCallsAssignments  - { [queueId]: { name, email } }
 */
export function buildQueueColumns({
    onUnlink,
    onLink,
    onAssignMissedCalls,
    missedCallsAssignments = {},
    matchedObjAssignments = {},
    matchedObjPending = {},
    onToggleMatchedObjAssignment,
}) {
    return [
        // ── Selection ────────────────────────────────────────────────────────
        columnHelper.display({
            id: "select",
            size: 40,
            enableSorting: false,
            header: ({ table }) => (
                <Checkbox
                    checked={
                        table.getIsAllRowsSelected()
                            ? true
                            : table.getIsSomeRowsSelected()
                                ? "mixed"
                                : false
                    }
                    onChange={table.getToggleAllRowsSelectedHandler()}
                    aria-label="Select all rows"
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    onChange={row.getToggleSelectedHandler()}
                    aria-label="Select row"
                />
            ),
        }),

        // ── Queue Name (hover → Queue Object ID tooltip) ──────────────────────
        columnHelper.accessor("queueName", {
            header: "Queue Name",
            sortingFn: "alphanumeric",
            cell: (info) => {
                const queueObjectId = info.row.original.queueObjectId;
                const name = info.getValue();
                if (!queueObjectId) return <strong>{name}</strong>;
                return (
                    <Tooltip
                        content={
                            <span>
                                <Text size={100} style={{ opacity: 0.75 }}>Queue Object ID</Text>
                                <br />
                                <Text size={200}>{queueObjectId}</Text>
                            </span>
                        }
                        relationship="description"
                        positioning="above-start"
                    >
                        <strong style={{ cursor: "default" }}>{name}</strong>
                    </Tooltip>
                );
            },
        }),

        // ── Linked Salesforce Org ─────────────────────────────────────────────
        columnHelper.accessor("linkedSalesforceInstanceUrl", {
            header: "Linked Salesforce Org",
            enableSorting: false,
            cell: (info) => {
                const instanceUrl = info.getValue();
                const orgId = info.row.original.linkedSalesforceOrgId;
                if (!instanceUrl) return <Text style={{ opacity: 0.45 }}>—</Text>;
                return (
                    <Tooltip
                        content={
                            <span>
                                <Text size={100} style={{ opacity: 0.75 }}>Org ID</Text>
                                <br />
                                <Text size={200}>{orgId}</Text>
                            </span>
                        }
                        relationship="description"
                        positioning="above-start"
                    >
                        <Text style={{ cursor: "default" }}>{instanceUrl}</Text>
                    </Tooltip>
                );
            },
        }),

        // ── Missed Call Owner (Missed Calls Assignee) ───────────────────────────
        columnHelper.display({
            id: "salesforceUser",
            header: "Missed Call Owner",
            enableSorting: false,
            cell: ({ row }) => {
                // Prefer server-side fields; fall back to the local assignments map
                // (populated by the optimistic update right after assigning).
                const local = missedCallsAssignments[row.original.queueId];
                const username = row.original.missedCallOwnerUsername ?? local?.username ?? local?.email ?? null;
                const ownerId = row.original.missedCallOwnerSfUserId ?? local?.sfUserId ?? null;

                if (!username) return <Text style={{ opacity: 0.45 }}>—</Text>;

                return (
                    <Tooltip
                        content={
                            <span>
                                <Text size={100} style={{ opacity: 0.75 }}>SF User ID</Text>
                                <br />
                                <Text size={200}>{ownerId ?? "—"}</Text>
                            </span>
                        }
                        relationship="description"
                        positioning="above-start"
                    >
                        <span
                            style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                cursor: "default",
                            }}
                        >
                            <Person16Regular
                                style={{ color: "#7160e8", flexShrink: 0 }}
                            />
                            <Text>{username}</Text>
                        </span>
                    </Tooltip>
                );
            },
        }),

        // ── Matched Record Assignment toggle ─────────────────────────────────
        columnHelper.display({
            id: "matchedObjAssignment",
            header: () => (
                <span>
                    Matched Record Assignment
                    {"\u00A0"}
                    <Tooltip
                        content={
                            <span>
                                Where a record has been matched to the inbound caller number,
                                the call record ownership will be assigned to the owner of the
                                matched record. Configure record matching in the{" "}
                                <a
                                    href="/admin?tab=salesforce"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: "inherit", textDecoration: "underline" }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    Salesforce Org screen
                                </a>
                                .
                            </span>
                        }
                        relationship="description"
                        positioning="above"
                        withArrow
                    >
                        <Button
                            appearance="transparent"
                            size="small"
                            icon={<Info16Regular />}
                            aria-label="Matched Record Assignment information"
                            style={{
                                minWidth: "auto",
                                padding: 0,
                                height: "16px",
                                color: "#616161",
                                cursor: "help",
                                verticalAlign: "text-bottom",
                            }}
                        />
                    </Tooltip>
                </span>
            ),
            enableSorting: false,
            size: 180,
            cell: ({ row }) => {
                const queueId = row.original.queueId;
                // Fall back to the server value so the toggle reflects the
                // persisted state before the local assignments map has been
                // seeded (the seeding effect runs after paint).
                const isOn =
                    matchedObjAssignments[queueId] ??
                    row.original.isObjectLinkingEnabled ??
                    false;
                // Disabled when: (a) the queue is not linked to a Salesforce org,
                // or (b) it is linked but the org has no Caller Number Lookup
                // Objects configured. The stored value is preserved either way.
                const isLinked = !!row.original.linkedSalesforceOrgId;
                const hasCliObjects = row.original.orgHasCliObjects === true;
                const isPending = matchedObjPending[queueId] === true;
                const isDisabled = !isLinked || !hasCliObjects || isPending;

                return (
                    <Tooltip
                        content={
                            isPending
                                ? "Saving\u2026"
                                : !isLinked
                                    ? "Link a Salesforce org to this queue to enable Matched Record Assignment"
                                    : !hasCliObjects
                                        ? "Configure Caller Number Lookup Objects for the linked Salesforce org first to enable Matched Record Assignment"
                                        : isOn
                                            ? "Matched Record Assignment is on"
                                            : "Matched Record Assignment is off"
                        }
                        relationship="label"
                        positioning="above"
                    >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 20 }}>
                            {isPending ? (
                                <Spinner style={{ marginLeft: "6px" }} size="tiny" aria-label="Saving Matched Record Assignment" />
                            ) : (
                                <Switch
                                    checked={isOn}
                                    disabled={isDisabled}
                                    onChange={(_, data) =>
                                        onToggleMatchedObjAssignment?.(queueId, data.checked)
                                    }
                                    aria-label="Matched Record Assignment"
                                />
                            )}
                        </span>
                    </Tooltip>
                );
            },
        }),

        // ── Actions ──────────────────────────────────────────────────────────
        columnHelper.display({
            id: "actions",
            header: "Actions",
            enableSorting: false,
            cell: ({ row }) => {
                const isLinked = !!row.original.linkedSalesforceOrgId;
                return (
                    <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                        {isLinked ? (
                            <Tooltip content="Unlink Salesforce Org" relationship="label" positioning="above">
                                <Button
                                    size="small"
                                    appearance="subtle"
                                    icon={<PlugDisconnected16Regular />}
                                    onClick={() => onUnlink(row.original)}
                                    style={{ color: "#c4341c" }}
                                    aria-label="Unlink Salesforce Org"
                                />
                            </Tooltip>
                        ) : (
                            <Tooltip content="Link Salesforce Org" relationship="label" positioning="above">
                                <Button
                                    size="small"
                                    appearance="subtle"
                                    icon={<Link16Regular />}
                                    onClick={() => onLink(row.original)}
                                    style={{ color: "#0f6cbd" }}
                                    aria-label="Link Salesforce Org"
                                />
                            </Tooltip>
                        )}
                        <Tooltip
                            content={
                                isLinked
                                    ? "Assign missed call owner for this queue"
                                    : "Link a Salesforce org first to enable missed call owner assignment"
                            }
                            relationship="label"
                            positioning="above"
                        >
                            {/* Wrap in span so tooltip works on disabled button */}
                            <span style={{ display: "inline-block" }}>
                                <Button
                                    size="small"
                                    appearance="subtle"
                                    icon={<PersonAdd16Regular />}
                                    disabled={!isLinked}
                                    onClick={() => isLinked && onAssignMissedCalls([row.original])}
                                    style={isLinked ? { color: "#7160e8" } : undefined}
                                    aria-label="Assign Missed Call Owner"
                                />
                            </span>
                        </Tooltip>
                    </div>
                );
            },
        }),
    ];
}